import { useEffect, useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import Textarea from '../ui/Textarea';

/**
 * Cadastrar navio (pelo IMO) ou editar tags / ETA informada / nota. As tags
 * são texto livre: Enter ou vírgula adiciona; as que já existem em outros
 * navios aparecem como atalho.
 */
const NavioFormModal = ({ isOpen, vessel = null, knownTags = [], saving = false, errors = {}, onClose, onSubmit }) => {
    const editing = Boolean(vessel);
    const [imo, setImo] = useState('');
    const [name, setName] = useState('');
    const [mmsi, setMmsi] = useState('');
    const [tags, setTags] = useState([]);
    const [tagDraft, setTagDraft] = useState('');
    const [etaInformed, setEtaInformed] = useState('');
    const [etaNote, setEtaNote] = useState('');
    const [note, setNote] = useState('');

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setImo(vessel?.imo || '');
        setName(vessel?.has_name ? vessel.name : '');
        setMmsi(vessel?.mmsi || '');
        setTags(vessel?.tags || []);
        setTagDraft('');
        setEtaInformed(vessel?.eta_informed || '');
        setEtaNote(vessel?.eta_informed_note || '');
        setNote(vessel?.note || '');
    }, [isOpen, vessel]);

    const addTag = (raw) => {
        const tag = String(raw || '')
            .replace(/\s+/g, ' ')
            .trim();
        if (tag && !tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) {
            setTags((current) => [...current, tag]);
        }
        setTagDraft('');
    };

    const submit = (event) => {
        event.preventDefault();
        const pendingTag = tagDraft.trim();
        const finalTags = pendingTag && !tags.includes(pendingTag) ? [...tags, pendingTag] : tags;
        onSubmit({
            ...(editing ? {} : { imo: imo.trim() }),
            name: name.trim() || null,
            mmsi: mmsi.trim() || null,
            tags: finalTags,
            eta_informed: etaInformed || null,
            eta_informed_note: etaNote.trim() || null,
            note: note.trim() || null,
        });
    };

    const suggestions = knownTags.filter(
        (tag) => !tags.some((existing) => existing.toLowerCase() === tag.toLowerCase()),
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={editing ? `Editar ${vessel.name}` : 'Novo navio'}
            size="lg"
            actions={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button type="submit" form="navio-form" loading={saving}>
                        {editing ? 'Salvar' : 'Cadastrar'}
                    </Button>
                </>
            }
        >
            <form id="navio-form" className="space-y-4" onSubmit={submit}>
                {editing ? null : (
                    <Input
                        label="IMO"
                        value={imo}
                        onChange={(event) => setImo(event.target.value)}
                        inputMode="numeric"
                        placeholder="9290464"
                        hint="7 dígitos. É a chave do navio — o MMSI muda com troca de bandeira, o IMO não."
                        errorMessage={errors.imo}
                        required
                        autoFocus
                    />
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        label="Nome (opcional)"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder="NORWALK"
                        hint={editing ? null : 'Se ficar vazio, o provider AIS preenche quando houver um.'}
                    />
                    <Input
                        label="MMSI (opcional)"
                        value={mmsi}
                        onChange={(event) => setMmsi(event.target.value)}
                        inputMode="numeric"
                        placeholder="636093305"
                        hint="9 dígitos. Muda com troca de bandeira."
                        errorMessage={errors.mmsi}
                    />
                </div>

                <div>
                    <label
                        htmlFor="navio-tag-input"
                        className="mb-1 block text-sm font-medium text-mono-700 dark:text-mono-300"
                    >
                        Tags
                    </label>
                    {tags.length > 0 ? (
                        <div className="mb-2 flex flex-wrap gap-1.5">
                            {tags.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => setTags((current) => current.filter((existing) => existing !== tag))}
                                    className="inline-flex items-center gap-1 rounded-full bg-black px-2.5 py-1 text-xs text-white dark:bg-white dark:text-black"
                                    aria-label={`Remover a tag ${tag}`}
                                >
                                    {tag} <span aria-hidden="true">×</span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                    <Input
                        id="navio-tag-input"
                        value={tagDraft}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value.endsWith(',')) {
                                addTag(value.slice(0, -1));
                            } else {
                                setTagDraft(value);
                            }
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                addTag(tagDraft);
                            }
                        }}
                        placeholder="PO002, fornecedor, agente de carga…"
                        hint="Enter ou vírgula adiciona. Toque numa tag para remover."
                    />
                    {suggestions.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {suggestions.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => addTag(tag)}
                                    className="rounded-full border border-dashed border-mono-300 px-2.5 py-1 text-xs text-mono-600 hover:bg-mono-50 dark:border-mono-700 dark:text-mono-400 dark:hover:bg-mono-900"
                                >
                                    + {tag}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        label="ETA informada"
                        type="date"
                        value={etaInformed}
                        onChange={(event) => setEtaInformed(event.target.value)}
                        hint="Previsão do agente de carga"
                    />
                    <Input
                        label="De onde veio a ETA"
                        value={etaNote}
                        onChange={(event) => setEtaNote(event.target.value)}
                        placeholder="HM Way · follow-up 08/09"
                    />
                </div>
                <Textarea label="Nota" value={note} onChange={(event) => setNote(event.target.value)} rows={3} />
            </form>
        </Modal>
    );
};

export default NavioFormModal;
